import { EAffiliateCampaignType, ECampaignStatus, ERejectedReason } from "@/modules/helpers/enum"
import type { Address } from "@/modules/types"
import type { SaleProp } from "@/modules/types/jobs/affiliate"
import type { AddressInfo } from "@/modules/types/jobs/campaign-form"

import { PHONE_REGEX } from "./validation"

// ========== Sale Props ==========
export function extractSaleProps(skus: any[]): SaleProp[] {
  const salePropMap: Record<string, SaleProp> = {}

  skus.forEach((sku) => {
    sku.skuSaleProps.forEach((saleProp: any) => {
      const { propId, propName, propValueId, propValue, image } = saleProp

      if (!salePropMap[propId]) {
        salePropMap[propId] = {
          propId,
          propName,
          hasImage: Boolean(image),
          salePropValues: []
        }
      }

      const valueExists = salePropMap[propId].salePropValues.some(
        (value: any) => value.propValueId === propValueId
      )

      if (!valueExists) {
        salePropMap[propId].salePropValues.push({
          propValueId,
          propValue,
          ...(image ? { image } : {})
        })
      }
    })
  })

  return Object.values(salePropMap)
}

/**
 * Filter sale props to only include values that exist in actual SKUs.
 * This prevents showing options like "3 Cuộn" when no SKU has that option.
 */
export function filterAvailableSaleProps(saleProps: SaleProp[], skus: any[]): SaleProp[] {
  if (!saleProps?.length || !skus?.length) return saleProps || []

  // Collect all propValueIds that exist in SKUs
  const existingPropValueIds = new Set<string>()
  skus.forEach((sku) => {
    sku?.skuSaleProps?.forEach((saleProp: any) => {
      if (saleProp?.propValueId) {
        existingPropValueIds.add(saleProp.propValueId.toString())
      }
    })
  })

  // Filter sale props to only include values that exist in SKUs
  return saleProps
    .map((prop) => ({
      ...prop,
      salePropValues: prop.salePropValues.filter((value: any) =>
        existingPropValueIds.has(value.propValueId?.toString())
      )
    }))
    .filter((prop) => prop.salePropValues.length > 0)
}

// ========== User Utils ==========
export function formatUserFullName(user?: { firstName?: string; lastName?: string }): string {
  if (!user?.firstName || !user?.lastName) return ""
  return `${user.lastName} ${user.firstName}`
}

export function hasAddressChanged(
  user: any,
  addressInfo: { address: string; phoneNumber: string; fullName: string }
): boolean {
  return (
    user?.profiles?.address !== addressInfo.address ||
    user?.phoneNumber !== addressInfo.phoneNumber ||
    formatUserFullName(user) !== addressInfo.fullName
  )
}

export function needsGMVCheck(user: any, isNeedGmv?: boolean): boolean {
  return (
    typeof user?.tiktoks?.liveGmv !== "number" &&
    typeof user?.tiktoks?.videoGmv !== "number" &&
    isNeedGmv === true
  )
}

// ========== Address Utils ==========
export const EMPTY_ADDRESS: AddressInfo = {
  address: "",
  phoneNumber: "",
  fullName: "",
  provinceCode: "",
  districtCode: "",
  wardCode: "",
  isDefault: false
}

export function buildAddressInfo(address?: Address | null): AddressInfo {
  if (!address) return { ...EMPTY_ADDRESS }
  return {
    address: address.address || "",
    phoneNumber: address.phoneNumber || "",
    fullName: address.fullName || "",
    provinceCode: address.provinces?.code || "",
    districtCode: address.districts?.code || "",
    wardCode: address.wards?.code || "",
    isDefault: address.isDefault || false
  }
}

export function formatAddress(address: Address): string {
  const parts = [address.address, address.wards?.name, address.provinces?.name]
  return parts.filter(Boolean).join(", ")
}

// ========== Button Status ==========
export enum ApplyButtonStatus {
  CAN_APPLY = "CAN_APPLY",
  APPLIED = "APPLIED",
  RESTRICT_CAMPAIGN = "RESTRICT_CAMPAIGN",
  RESTRICT_ACCOUNT = "RESTRICT_ACCOUNT",
  NOT_AUTHORIZE_SHOW_CASE = "NOT_AUTHORIZE_SHOW_CASE",
  FINISHED = "FINISHED",
  PAUSED = "PAUSED"
}

export function isUserRestricted(user: any, rejectedReasons?: string[]): boolean {
  return (
    !!user?.isRestricted ||
    rejectedReasons?.includes(ERejectedReason.RESTRICTED_DUE_TO_OVERDUE_CAMPAIGNS) === true
  )
}

export function resolveButtonStatus({
  status,
  isApplied,
  isRestricted,
  isAccountRestricted,
  requireShowcaseAuthorization,
  kolSubmissionStatus
}: {
  status?: ECampaignStatus
  isApplied?: boolean
  isRestricted?: boolean
  isAccountRestricted?: boolean
  requireShowcaseAuthorization?: boolean
  kolSubmissionStatus?: string
}): ApplyButtonStatus {
  if (isApplied || kolSubmissionStatus === "APPLIED") return ApplyButtonStatus.APPLIED
  if (status === ECampaignStatus.FINISHED) return ApplyButtonStatus.FINISHED
  if (status === ECampaignStatus.PAUSED) return ApplyButtonStatus.PAUSED
  if (requireShowcaseAuthorization) return ApplyButtonStatus.NOT_AUTHORIZE_SHOW_CASE
  if (isRestricted) {
    return isAccountRestricted
      ? ApplyButtonStatus.RESTRICT_ACCOUNT
      : ApplyButtonStatus.RESTRICT_CAMPAIGN
  }
  return ApplyButtonStatus.CAN_APPLY
}

// ========== Validation ==========

export type ValidationErrors = {
  address?: string
  fullName?: string
  phoneNumber?: string
  contentTypes?: string
  totalClip?: string
  selectedSkuId?: string
}

export type ValidationResult = {
  isValid: boolean
  errors: ValidationErrors
}

export function validateFormData({
  contentTypes,
  totalClip
}: {
  addressInfo?: AddressInfo
  selectedAddress?: any
  contentTypes: EAffiliateCampaignType[]
  totalClip: string
  productHasSkus?: boolean
  selectedSkuId?: string
}): ValidationResult {
  const errors: ValidationErrors = {}

  if (!contentTypes.length) {
    errors.contentTypes = "Vui lòng chọn ít nhất một hình thức (Video hoặc Livestream)"
  }

  if (!totalClip) {
    errors.totalClip = "Vui lòng chọn số lượng video/livestream"
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

export function validateFormDataLegacy({
  addressInfo,
  selectedAddress,
  contentTypes,
  totalClip,
  productHasSkus,
  selectedSkuId,
  isMultipleSkuMode,
  selectedSkus
}: {
  addressInfo: AddressInfo
  selectedAddress: any
  contentTypes: EAffiliateCampaignType[]
  totalClip: string
  productHasSkus: boolean
  selectedSkuId: string
  isMultipleSkuMode?: boolean
  selectedSkus?: Record<string, Record<string, string>>
}): ValidationResult {
  const errors: ValidationErrors = {}

  if (!selectedAddress && !addressInfo.address) {
    errors.address = "Vui lòng chọn hoặc thêm địa chỉ nhận hàng"
  }

  if (selectedAddress || addressInfo.address) {
    if (!addressInfo.fullName.trim()) {
      errors.fullName = "Vui lòng nhập họ và tên"
    }

    if (!addressInfo.phoneNumber.trim()) {
      errors.phoneNumber = "Vui lòng nhập số điện thoại"
    } else if (!PHONE_REGEX.test(addressInfo.phoneNumber.replace(/\D/g, ""))) {
      errors.phoneNumber = "Số điện thoại không hợp lệ"
    }
  }

  if (!contentTypes.length) {
    errors.contentTypes = "Vui lòng chọn ít nhất một hình thức (Video hoặc Livestream)"
  }

  if (!totalClip) {
    errors.totalClip = "Vui lòng chọn số lượng video/livestream"
  }

  // Validate SKU selection based on mode
  if (isMultipleSkuMode) {
    // Multiple SKU mode: check if at least one SKU is selected
    const hasAnySelection = selectedSkus && Object.keys(selectedSkus).length > 0
    if (!hasAnySelection) {
      errors.selectedSkuId = "Vui lòng chọn ít nhất một phân loại sản phẩm"
    }
  } else if (productHasSkus && !selectedSkuId) {
    // Single SKU mode: check if SKU is selected
    errors.selectedSkuId = "Vui lòng chọn phân loại sản phẩm"
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}
